import {Injectable} from '@angular/core';
import {HttpClient, HttpRequest, HttpEventType, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Subject} from 'rxjs';
import {environment} from '../../../environments/environment';
import {Image} from '../../model/Image';
import {Xml} from "../../model/Xml";
import {Asset} from "../../model/Asset";
import {Metadata} from "../../model/Metadata";
import {Language} from "../../model/Language";
import {Model} from "../../model/Model";
import {Pdf} from "../../model/Pdf";
import {Checklist} from "../../model/Checklist";
import {Disclaimer} from "../../model/Disclaimer";

const urlImagesUpload = '/upload/images';
const urlTotalImageCount = '/upload/images/count';
const urlXmlUpload = '/upload/xml';
const urlLangXmlUpload = '/upload/langxml';
const urlPdfUpload = '/upload/pdf';
const urlAssetDmCoverUpload = '/upload/asset/dm-cover';
const urlAssetVideoCoverUpload = '/upload/asset/video-cover';
const urlAssetVideoLoopUpload = '/upload/asset/video-loop';
const urlAssetHotspotCoverUpload = '/upload/asset/hotspot-cover';
const urlAssetUsefulTipsCoverUpload = '/upload/asset/usefultips-cover';
const urlSubmitPackage = '/submit';

@Injectable({
  providedIn: 'root'
})
export class FileuploadService {

  urlMetadataUpload: string = '/upload/metadata';
  urlImagesUpload: string = '/upload/images';
  urlDisclaimerUpload: string = '/upload/disclaimer';

  images: Observable<Image[]>;
  public currentPackageId = new Subject();

  constructor(private http: HttpClient) {
  }

  public setCurrentPackageId(currentPackageId) {
    this.currentPackageId.next(currentPackageId);
  }

  public uploadImages(files: Set<File>, pkgId: number): { [key: string]: Observable<number> } {

    const status = {};

    files.forEach(file => {

      const formData: FormData = new FormData();
      formData.append('file', file, file.name);

      const req = new HttpRequest('POST', `${environment.packagesManagerBackendUrl}` + urlImagesUpload + '/' + pkgId, formData, {
        reportProgress: true
      });


      const progress = new Subject<number>();

      // send the http-request and subscribe for progress-updates
      this.http.request(req).subscribe(event => {
        if (event.type === HttpEventType.UploadProgress) {

          const percentDone = Math.round(100 * event.loaded / event.total);

          // pass the percentage into the progress-stream
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          progress.complete();
        }
      });

      status[file.name] = {
        progress: progress.asObservable()
      };
    });

    // return the map of progress.observables
    return status;
  }

  public fetchImageList(page: number, pkgId: number): Observable<Image[]> {
    return this.images = this.http.get<Image[]>(`${environment.packagesManagerBackendUrl}` + urlImagesUpload + '/' + pkgId + '/' + page);
  }

  public getTotalImageCount(pkgId: number): Observable<number> {
    return this.http.get<number>(`${environment.packagesManagerBackendUrl}` + urlTotalImageCount + '/' + pkgId);
  }

  public fetchXml(pkgId: number): Observable<Xml[]> {
    return this.http.get<Xml[]>(`${environment.packagesManagerBackendUrl}` + urlXmlUpload + '/' + pkgId);
  }

  public fetchLangXml(pkgId: number): Observable<Xml[]> {
    return this.http.get<Xml[]>(`${environment.packagesManagerBackendUrl}` + urlLangXmlUpload + '/' + pkgId);
  }

  public fetchPdf(pkgId: number): Observable<Xml[]> {
    return this.http.get<Pdf[]>(`${environment.packagesManagerBackendUrl}` + urlPdfUpload + '/' + pkgId);
  }

  public uploadMetadata(metadata: Metadata, currentPackageId): Observable<any> {
    return this.http.post(`${environment.packagesManagerBackendUrl}` + this.urlMetadataUpload + '/' + currentPackageId, metadata);
  }

  public getMetadata(currentPackageId): Observable<any> {
    return this.http.get<Metadata>(`${environment.packagesManagerBackendUrl}` + this.urlMetadataUpload + '/' + currentPackageId);
  }

  public deleteImage(pkgId: number, imageName: string): Observable<any> {
    return this.http.delete(`${environment.packagesManagerBackendUrl}` + this.urlImagesUpload + '/' + pkgId + '/' + imageName);
  }

  public getLanguages(): Observable<any> {
    return this.http.get<Language>(`${environment.packagesManagerBackendUrl}` + '/languages');
  }

  public getModels(): Observable<any> {
    return this.http.get<Model>(`${environment.packagesManagerBackendUrl}` + '/models');
  }

  public fetchAppAssetDmCover(pkgId: number): Observable<Xml[]> {
    return this.http.get<Asset[]>(`${environment.packagesManagerBackendUrl}` + urlAssetDmCoverUpload + '/' + pkgId);
  }

  public fetchAppAssetVideoCover(pkgId: number): Observable<Xml[]> {
    return this.http.get<Asset[]>(`${environment.packagesManagerBackendUrl}` + urlAssetVideoCoverUpload + '/' + pkgId);
  }

  public fetchAppAssetHotspotCover(pkgId: number): Observable<Xml[]> {
    return this.http.get<Asset[]>(`${environment.packagesManagerBackendUrl}` + urlAssetHotspotCoverUpload + '/' + pkgId);
  }

  public fetchAppAssetUsefulTipsCover(pkgId: number): Observable<Xml[]> {
    return this.http.get<Asset[]>(`${environment.packagesManagerBackendUrl}` + urlAssetUsefulTipsCoverUpload + '/' + pkgId);
  }

  public fetchAppAssetVideoLoop(pkgId: number): Observable<Xml[]> {
    return this.http.get<Asset[]>(`${environment.packagesManagerBackendUrl}` + urlAssetVideoLoopUpload + '/' + pkgId);
  }

  public getDisclaimer(pkgId): Observable<any> {
    return this.http.get<Disclaimer>(`${environment.packagesManagerBackendUrl}` + this.urlDisclaimerUpload + '/' + pkgId);
  }


  public uploadDisclaimer(disclaimer: Disclaimer, currentPackageId): Observable<any> {
    return this.http.post(`${environment.packagesManagerBackendUrl}` + this.urlDisclaimerUpload + '/' + currentPackageId, disclaimer);
  }

  /**
   *
   * TODO
   */
  public getDisclaimerTemplateList(): Observable<any> {
    return this.http.get<Disclaimer[]>(`${environment.packagesManagerBackendUrl}` +'/upload/template/disclaimer/' );
  }
  public getDisclaimerTemplate(templateName:string): Observable<any> {
    return this.http.get<Disclaimer>(`${environment.packagesManagerBackendUrl}` +'/upload/template/disclaimer/load/'+templateName );
  }
  public uploadDisclaimerTemplate(disclaimer: Disclaimer, templateName:string): Observable<any> {

    let url=`${environment.packagesManagerBackendUrl}` + '/upload/template/disclaimer/' + templateName;
    console.log(url);
    return this.http.post(url, disclaimer);
  }

  public deleteDisclaimerTemplate(disclaimerTemplateName: string): Observable<any> {
    return this.http.delete(`${environment.packagesManagerBackendUrl}` + '/upload/template/disclaimer/' + disclaimerTemplateName);
  }


  public getXmlValidation(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/xml/' + pkgId);
  }

  public getMetadataValidation(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/metadata/' + pkgId);
  }

  public getPdfValidation(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/pdf/' + pkgId);
  }

  public startImagesValidation(pkgId: number): Observable<any> {
    return this.http.get(`${environment.packagesManagerBackendUrl}` + '/validation/images/' + pkgId);
  }

  public isImagesValidationInProcess(pkgId: number): Observable<any> {
    return this.http.get(`${environment.packagesManagerBackendUrl}` + '/validation/images-inprocess/' + pkgId);
  }

  public getImagesValidationResult(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/images-result/' + pkgId);
  }

  public getAppAssetsValidation(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/app-assets/' + pkgId);
  }

  public getDisclaimerValidation(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/disclaimer/' + pkgId);
  }

  public getHotspotValidation(pkgId: number): Observable<any> {
    return this.http.get<Checklist[]>(`${environment.packagesManagerBackendUrl}` + '/validation/hotspot/' + pkgId);
  }

  public postSubmittedPackage(data: any, pkgId: number): Observable<any> {
    return this.http.post(`${environment.packagesManagerBackendUrl}` + urlSubmitPackage + '/' + pkgId, data);
  }
}

